import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { requestNotificationPermission } from "../lib/useUserNotifications";
import { registerPushSubscription } from "../lib/webPush";

const DISMISS_KEY = "tw_notify_banner_dismissed";

export default function NotificationPermissionBanner() {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !("Notification" in window)) return;
        if (localStorage.getItem(DISMISS_KEY) === "1") return;
        if (Notification.permission === "default") setShow(true);
    }, []);

    if (!show) return null;

    const onEnable = async () => {
        setBusy(true);
        const result = await requestNotificationPermission();
        if (result === "granted") {
            // Also try to subscribe to WebPush so notifications work when tab is closed.
            try { await registerPushSubscription(); } catch { /* best effort */ }
            toast.success(t("notify.permission_granted"));
            setShow(false);
        } else if (result === "denied") {
            toast.error(t("notify.permission_denied"));
            setShow(false);
        } else if (result === "unsupported") {
            toast.error(t("notify.permission_unsupported"));
            setShow(false);
        }
        setBusy(false);
    };

    const onDismiss = () => {
        localStorage.setItem(DISMISS_KEY, "1");
        setShow(false);
    };

    return (
        <div
            className="bg-[#FFCC00] text-[#111827] rounded-sm p-4 mb-4 flex items-start gap-3 border-l-4 border-[#111827]"
            data-testid="notify-permission-banner"
        >
            <Bell className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
                <div className="font-bold text-sm uppercase tracking-wider">{t("notify.banner_title")}</div>
                <p className="text-sm mt-1 leading-relaxed">{t("notify.banner_body")}</p>
                <div className="mt-3 flex gap-2">
                    <Button
                        type="button"
                        onClick={onEnable}
                        disabled={busy}
                        className="bg-[#111827] hover:bg-black text-white rounded-sm h-9 text-xs font-bold uppercase tracking-wider"
                        data-testid="notify-banner-enable"
                    >
                        {t("notify.banner_enable")}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onDismiss}
                        className="hover:bg-black/10 rounded-sm h-9 text-xs font-bold uppercase tracking-wider"
                        data-testid="notify-banner-dismiss"
                    >
                        {t("notify.banner_dismiss")}
                    </Button>
                </div>
            </div>
            <button
                type="button"
                onClick={onDismiss}
                className="text-[#111827]/60 hover:text-[#111827]"
                data-testid="notify-banner-close"
                aria-label="close"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
